import { InputHTMLAttributes, ReactNode, forwardRef } from "react";
import clsx from "clsx";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  variant?: "default" | "filled" | "outlined";
  size?: "sm" | "md" | "lg";
  error?: boolean;
  helperText?: string;
  icon?: ReactNode;
  suffix?: ReactNode;
}

/**
 * TracQ Brand Input-Komponente (Tailwind v4)
 * - Variants: default, filled, outlined
 * - Sizes: sm, md, lg
 * - Features: error states, helper text
 * - TracQ Brand Colors & Typography
 * - Dark Mode optimiert & barrierefrei
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant = "default",
      size = "md",
      error = false,
      helperText,
      icon,
      suffix,
      ...props
    },
    ref,
  ) => {
    const variantStyles = {
      default:
        "bg-gray-900 border-gray-700 focus:border-tracq-bright-blue focus:ring-tracq-bright-blue/20",
      filled:
        "bg-gray-800 border-transparent focus:border-tracq-bright-blue focus:ring-tracq-bright-blue/20",
      outlined:
        "bg-transparent border-2 border-tracq-deep-blue focus:border-tracq-bright-blue focus:ring-tracq-bright-blue/20",
    };

    const sizeStyles = {
      sm: "px-3 py-1.5 text-body-sm",
      md: "px-4 py-2 text-body",
      lg: "px-5 py-3 text-subheading",
    };

    const errorStyles = error
      ? "border-destructive focus:border-destructive focus:ring-destructive/20"
      : "";

    return (
      <div className="w-full">
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            className={clsx(
              // TracQ Base Styles
              "w-full rounded-tracq font-poppins",
              "text-white placeholder-gray-400",
              "border transition-all duration-200 ease-in-out",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-800",

              // Variant & Size Styles
              variantStyles[variant],
              sizeStyles[size],

              // Error State
              errorStyles,

              // Icon padding
              icon && "pl-10",

              // Suffix padding
              suffix && "pr-10",

              className,
            )}
            {...props}
          />
          {suffix && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {suffix}
            </div>
          )}
        </div>

        {/* TracQ Helper Text */}
        {helperText && (
          <p
            className={clsx(
              "mt-1.5 text-body-sm font-poppins",
              error ? "text-destructive" : "text-gray-400",
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
